import { useUserIdWithLoading } from '@dreamstack/auth'
import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { ChevronDown } from '@dreamstack/icons/src/icon-components/ChevronDown'
import { ChevronUp } from '@dreamstack/icons/src/icon-components/ChevronUp'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util'
import {
  PROPERTY_STATUS_FREE,
  PropertyStatusOpenReservation,
} from '@dreamstack/real-estate-logic'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { useParsePropertyStatus } from '../hooks/usePropertyStatus'
import { usePropertyPriceVisibility } from '../hooks/usePropertyPriceVisibility'
import { SimpleIconButton } from '../layout/SimpleIconButton'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { MoreFlatsSmallText } from '../layout/Texts'
import { blurTextWithMargin } from '../styles/styles'
import { sortPropertiesByFloor } from './helper/sortPropertiesByStatus'
import { useGetPropertyUrl } from './routing/useGetPropertyUrl'

const TableColTitle = ({
  align,
  children,
  className = '',
}: React.PropsWithChildren<{
  align?: 'left' | 'center' | 'right'
  className?: string
}>) => (
  <p
    className={
      `whitespace-nowrap font-semibold text-accentroGray-full ml:text-xl p-px-12 ` +
      (align === 'left'
        ? 'text-left'
        : align === 'center'
        ? 'text-center'
        : align === 'right'
        ? 'text-right'
        : '') +
      ' ' +
      className
    }
  >
    {children}
  </p>
)

const TableColText = ({
  align,
  children,
  className = '',
}: React.PropsWithChildren<{
  align?: 'left' | 'center' | 'right'
  className?: string
}>) => (
  <div
    className={
      `whitespace-nowrap p-px-12 h-full flex flex-row items-center text-base ml:text-xl ` +
      (align === 'left'
        ? 'justify-start'
        : align === 'center'
        ? 'justify-center'
        : align === 'right'
        ? 'justify-end'
        : '') +
      ' ' +
      className
    }
  >
    {children}
  </div>
)

const TableRow = ({
  idx,
  children,
}: React.PropsWithChildren<{ idx: number }>) => (
  <div
    className={
      'contents ' + (idx % 2 ? 'all-child:bg-accentroAqua-100 bg-black' : '')
    }
  >
    {children}
  </div>
)

const StyledStatus = ({
  status,
  children,
}: React.PropsWithChildren<{ status: number }>) => {
  const cls = PropertyStatusOpenReservation.includes(status)
    ? 'bg-accentroYellowLight-full text-accentroSecondaryBrown-full'
    : PROPERTY_STATUS_FREE.includes(status)
    ? 'bg-accentroSuccessLight-full text-accentroSuccess-full'
    : 'bg-accentroError-100 text-accentroError-full'
  return <div className={`px-px-6 font-semibold ${cls}`}>{children}</div>
}

const StyledStatusCircle = ({ status }: { status: number }) => {
  const cls = PropertyStatusOpenReservation.includes(status)
    ? 'bg-accentroYellowLight-full'
    : PROPERTY_STATUS_FREE.includes(status)
    ? 'bg-accentroSuccess-full'
    : 'bg-accentroError-full'
  return <div className={`w-px-16 h-px-16 rounded-full ${cls}`} />
}

const StyledPropertyTable = ({
  showAllProperties,
  children,
}: React.PropsWithChildren<{ showAllProperties: boolean }>) => (
  <div
    className={
      'overflow-y-hidden ' +
      (showAllProperties ? 'max-h-none' : 'max-h-[300px]')
    }
  >
    {children}
  </div>
)

export const MoreFlatsList: FunctionComponent<
  React.PropsWithChildren<{
    properties: PropertyPreview[]
  }>
> = ({ properties }) => {
  const [showAllProperties, setShowAllProperties] = useState(false)
  // Sorting priorities by Floor
  properties = sortPropertiesByFloor(properties)
  const formatCurrency = useFormatToCurrencyCutoff()
  const formatDecimal = useFormatToDecimal()
  const t = useTranslation()
  const { userId } = useUserIdWithLoading()
  const getPropertyUrl = useGetPropertyUrl()
  const parsePropertyStatus = useParsePropertyStatus()
  const propertyPriceVisibility = usePropertyPriceVisibility()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()

  const trackClickCallToActionButton = async ({
    propertyId,
  }: {
    propertyId: number
  }) => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Weitere Wohnungen',
        url: router.asPath,
        additionalInfo: {
          propertyId,
        },
      },
    })
  }

  return (
    <div className="mt-px-40 col-span-full ml:mt-px-80 w-full flex flex-col gap-6">
      <div className="shadow-[rgb(0_0_0/10%)_-10px_0px_10px_-5px_inset] sm:shadow-none">
        <StyledPropertyTable showAllProperties={showAllProperties}>
          <div className="w-full grid grid-cols-[repeat(3,min-content)_auto_repeat(2,min-content)] md:grid-cols-[repeat(4,min-content)_auto_repeat(2,min-content)] items-end">
            <div className="contents [&>*]:border-b-2 [&>*]:border-accentroGray-200">
              <TableColTitle align="left" className="hidden md:block">
                ID
              </TableColTitle>
              <TableColTitle align="center">
                {t('accentro:moreFlatsList.floor')}
              </TableColTitle>
              <TableColTitle align="center">
                {t('accentro:moreFlatsList.rooms')}
              </TableColTitle>
              <TableColTitle align="center">
                {t('accentro:moreFlatsList.livingSpace')}
              </TableColTitle>
              <TableColTitle align="right">
                {t('accentro:moreFlatsList.price')}
              </TableColTitle>
              <TableColTitle align="center">Status</TableColTitle>
              <div className="h-full" />
            </div>
            {properties.map((property, idx) => {
              const formattedStatus = parsePropertyStatus(property.status)
              const formattedPrice = propertyPriceVisibility(property)
                ? formatCurrency(property.price!)
                : '???'
              const formattedSqm = property.property_space_value
                ? formatDecimal(property.property_space_value)
                : ''

              const floorNum = property.floor
                ? parseInt(property.floor) === 0
                  ? t('accentro:property.groundFloorAbbreviation')
                  : property.floor + '.'
                : null

              const count = property.number_of_rooms ?? 0

              return (
                <TableRow idx={idx} key={property.name}>
                  <TableColText align="left" className="hidden md:flex">
                    {property.name}
                  </TableColText>
                  <TableColText align="center">{floorNum ?? '-'}</TableColText>
                  <TableColText align="center">{count}</TableColText>
                  <TableColText align="center">
                    <span>
                      {formattedSqm} {t('accentro:squareMeter')}
                    </span>
                  </TableColText>
                  <TableColText align="right">
                    <span
                      className={cn(
                        'relative font-semibold text-accentroGray-full',
                        !propertyPriceVisibility(property) && blurTextWithMargin
                      )}
                    >
                      {formattedPrice}{' '}
                      <MoreFlatsSmallText>EUR</MoreFlatsSmallText>
                    </span>
                  </TableColText>
                  <TableColText align="center">
                    <div className="hidden md:block">
                      <StyledStatus status={property.status as number}>
                        {formattedStatus}
                      </StyledStatus>
                    </div>
                    <div className="md:hidden">
                      <StyledStatusCircle status={property.status as number} />
                    </div>
                  </TableColText>
                  <TableColText>
                    <Link
                      href={getPropertyUrl({
                        id: property.id,
                        city: property.city ?? undefined,
                      })}
                    >
                      <SimpleIconButton
                        aria-label="View more details"
                        onClick={() =>
                          trackClickCallToActionButton({
                            propertyId: property.id,
                          })
                        }
                      >
                        <ArrowRight className="text-black" />
                      </SimpleIconButton>
                    </Link>
                  </TableColText>
                </TableRow>
              )
            })}
          </div>
        </StyledPropertyTable>
      </div>
      {properties.length > 3 && (
        <div className="flex justify-center items-center">
          <SimpleMuiButton
            endIcon={showAllProperties ? <ChevronUp /> : <ChevronDown />}
            onClick={() => setShowAllProperties(!showAllProperties)}
          >
            {showAllProperties
              ? t('accentro:moreFlatsList.lessApartments')
              : t('accentro:moreFlatsList.allApartments')}
          </SimpleMuiButton>
        </div>
      )}
    </div>
  )
}
